import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BaseComponent } from '../base-component/base-component';
import variables from '../../common/styles/variables';
import { ifDefined } from 'lit/directives/if-defined.js';

/**
 * A circular person avatar component that can display either an image or initials.
 * 
 * @csspart container - The main container div
 * @csspart image - The avatar image element (when image is displayed)
 * @csspart initials - The initials text element (when initials are displayed)
 */
@customElement('mhecomm-person-avatar')
export class PersonAvatar extends BaseComponent {
  static styles = [
    variables,
    css`
      :host {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .avatar-container {
        display: flex;
        width: 120px;
        height: 120px;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        overflow: hidden;
        background-color: var(--mh-light-gray);
        color: var(--mh-white);
        font-weight: 600;
        text-transform: uppercase;
        position: relative;
      }

      .avatar-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 50%;
      }

      .avatar-initials {
        color: white;
        font-weight: 500;
        text-align: center;
        font-size: 64px;
        line-height: 1;
        user-select: none;
      }

      .avatar-container:focus-visible {
        outline: var(--mh-outline);
        outline-offset: var(--mh-outline-offset);
      }
    `
  ];

  /**
   * The URL of the person's image
   */
  @property()
  imageUrl = '';

  /**
   * The person's full name to generate initials from
   */
  @property()
  name = '';

  /**
   * Alternative text for the avatar image
   */
  @property()
  altText = '';

  @state()
  private imageLoadError = false;

  private handleImageLoad() {
    this.imageLoadError = false;
  }

  private handleImageError() {
    this.imageLoadError = true;
  }

  private shouldShowImage(): boolean {
    return !!(this.imageUrl && !this.imageLoadError);
  }

  private getInitials(): string {
    if (this.shouldShowImage()) {
      return '';
    }
    
    return this.name.substring(0, 1).toUpperCase();
  }

  private getAriaLabel(): string {
    if (this.altText) {
      return this.altText;
    }
    
    if (this.name) {
      return `Avatar for ${this.name}`;
    }
    
    return 'Person avatar';
  }

  renderComp() {
    return html`
      <div 
        class="avatar-container"
        part="container"
        tabindex="-1"
        role="img"
        aria-label="${this.getAriaLabel()}">
        ${this.shouldShowImage() ? html`
          <img
            src="${this.imageUrl}"
            alt="${ifDefined(this.altText || (this.name ? `Avatar for ${this.name}` : undefined))}"
            class="avatar-image"
            part="image"
            @load="${this.handleImageLoad}"
            @error="${this.handleImageError}"
          />
        ` : html`
          <span
            class="avatar-initials"
            part="initials"
            aria-hidden="true">
            ${this.getInitials() || '?'}
          </span>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-person-avatar': PersonAvatar;
  }
}
